/**
 * Comments update — pure state transition logic.
 *
 * Figma authority: R4VYIdXEv7HYIXtpDMEvHY, node 38:2510
 *
 * Elm-ish architecture: this function is pure.
 * - No API calls
 * - No storage access
 * - No hooks
 * - No side effects
 * - No mutation
 * - No Date.now(), Math.random(), UUID, storage, or browser APIs
 *
 * Each message produces a new model and any effects that need to run.
 *
 * Load generations:
 * - LoadStarted carries a loadId
 * - LoadSucceeded/LoadNotFound/LoadFailed/CurrentUserLoaded carry loadId
 * - Stale load completions (msg.loadId !== model.activeLoadId) are rejected
 */
import type { CommentsModel } from './model';
import type { CommentsMsg } from './messages';

// =============================================================================
// Exhaustiveness helper
// =============================================================================

/**
 * Compile-time exhaustiveness check.
 * If this is reached, a message variant is not handled.
 * Does NOT include user content.
 */
function assertNever(value: never): never {
  throw new Error(`Unhandled comments message: ${String(value)}`);
}

// =============================================================================
// Effect types
// =============================================================================

/**
 * Effect types that can be triggered by state transitions.
 * These are handled by the effect runner (useCommentsProgram.ts).
 */
export type CommentsEffect =
  /** Load post and comments data from the API */
  | { readonly type: 'LoadComments'; readonly loadId: number; readonly postId: string }
  /** Load current user profile for composer avatar */
  | { readonly type: 'LoadCurrentUser'; readonly loadId: number }
  /** Submit a new comment to the API */
  | {
      readonly type: 'SubmitComment';
      readonly submissionId: number;
      readonly postId: string;
      readonly content: string;
    }
  /** Toggle like on a comment */
  | { readonly type: 'ToggleLike'; readonly commentId: string };

// =============================================================================
// Update result
// =============================================================================

/**
 * Result of an update call — new model plus effects to run.
 */
export interface UpdateResult {
  readonly model: CommentsModel;
  readonly effects: readonly CommentsEffect[];
}

// =============================================================================
// Pure update function
// =============================================================================

/**
 * Pure update function — the heart of the Elm-ish architecture.
 *
 * @param model — Current state
 * @param msg — Message triggering the transition
 * @returns New state and effects to run
 */
export function update(
  model: CommentsModel,
  msg: CommentsMsg
): UpdateResult {
  switch (msg.type) {
    case 'LoadStarted':
      // LoadStarted always wins — replaces the current route.
      // Resets all route-scoped state including submission/composer state.
      return {
        model: {
          ...model,
          status: 'loading',
          error: null,
          rootPost: null,
          comments: [],
          activeLoadId: msg.loadId,
          activeSubmissionId: null,
          composer: {
            draft: '',
            isSubmitting: false,
            hasFailed: false,
          },
        },
        effects: [
          { type: 'LoadComments', loadId: msg.loadId, postId: msg.postId },
          { type: 'LoadCurrentUser', loadId: msg.loadId },
        ],
      };

    case 'LoadSucceeded':
      // Reject stale load completions
      if (msg.loadId !== model.activeLoadId) {
        return { model, effects: [] };
      }
      return {
        model: {
          ...model,
          status: 'ready',
          rootPost: msg.post,
          comments: msg.comments,
          error: null,
        },
        effects: [],
      };

    case 'LoadNotFound':
      // Reject stale load completions
      if (msg.loadId !== model.activeLoadId) {
        return { model, effects: [] };
      }
      return {
        model: {
          ...model,
          status: 'not_found',
          rootPost: null,
          comments: [],
          error: null,
        },
        effects: [],
      };

    case 'LoadFailed':
      // Reject stale load completions
      if (msg.loadId !== model.activeLoadId) {
        return { model, effects: [] };
      }
      return {
        model: {
          ...model,
          status: 'error',
          rootPost: null,
          comments: [],
          error: msg.error,
        },
        effects: [],
      };

    case 'CurrentUserLoaded':
      // Reject stale load completions
      if (msg.loadId !== model.activeLoadId) {
        return { model, effects: [] };
      }
      return {
        model: {
          ...model,
          currentUser: msg.user,
        },
        effects: [],
      };

    case 'DraftChanged':
      return {
        model: {
          ...model,
          composer: {
            ...model.composer,
            draft: msg.draft,
            hasFailed: false,
          },
        },
        effects: [],
      };

    case 'SendClicked': {
      // Guard: no action if not in ready state
      if (model.status !== 'ready') {
        return { model, effects: [] };
      }
      // Guard: no action if a submission is already in-flight
      if (model.activeSubmissionId !== null) {
        return { model, effects: [] };
      }
      // Guard: draft must be non-empty
      const trimmedDraft = model.composer.draft.trim();
      if (trimmedDraft.length === 0) {
        return { model, effects: [] };
      }
      // Guard: we must have a root post
      if (model.rootPost === null) {
        return { model, effects: [] };
      }
      // Generate deterministic submission identity from pure counter
      const submissionId = model.nextSubmissionId;
      return {
        model: {
          ...model,
          composer: {
            ...model.composer,
            isSubmitting: true,
            hasFailed: false,
          },
          activeSubmissionId: submissionId,
          nextSubmissionId: submissionId + 1,
        },
        effects: [
          {
            type: 'SubmitComment',
            submissionId,
            postId: model.rootPost.id,
            content: trimmedDraft,
          },
        ],
      };
    }

    case 'SendSucceeded': {
      // Guard: ignore stale completions
      if (model.activeSubmissionId !== msg.submissionId) {
        return { model, effects: [] };
      }
      return {
        model: {
          ...model,
          composer: {
            draft: '',
            isSubmitting: false,
            hasFailed: false,
          },
          comments: [...model.comments, msg.comment],
          activeSubmissionId: null,
          error: null,
        },
        effects: [],
      };
    }

    case 'SendFailed': {
      // Guard: ignore stale completions
      if (model.activeSubmissionId !== msg.submissionId) {
        return { model, effects: [] };
      }
      return {
        model: {
          ...model,
          composer: {
            ...model.composer,
            isSubmitting: false,
            hasFailed: true,
          },
          activeSubmissionId: null,
          error: msg.error,
        },
        effects: [],
      };
    }

    case 'LikeClicked': {
      // Guard: no action if not in ready state
      if (model.status !== 'ready') {
        return { model, effects: [] };
      }
      // Guard: comment must exist
      const commentIndex = model.comments.findIndex(c => c.id === msg.commentId);
      if (commentIndex === -1) {
        return { model, effects: [] };
      }
      // Optimistic update: toggle the like state
      const comment = model.comments[commentIndex];
      const newLiked = !comment.likedByCurrentUser;
      const newCount = newLiked ? comment.likeCount + 1 : Math.max(0, comment.likeCount - 1);
      const updatedComment: typeof comment = {
        ...comment,
        likedByCurrentUser: newLiked,
        likeCount: newCount,
      };
      const updatedComments = [
        ...model.comments.slice(0, commentIndex),
        updatedComment,
        ...model.comments.slice(commentIndex + 1),
      ];
      return {
        model: {
          ...model,
          comments: updatedComments,
        },
        effects: [{ type: 'ToggleLike', commentId: msg.commentId }],
      };
    }

    case 'LikeSucceeded': {
      // Reconcile with canonical server response
      const idx = model.comments.findIndex(c => c.id === msg.commentId);
      if (idx === -1) {
        return { model, effects: [] };
      }
      const updated = [
        ...model.comments.slice(0, idx),
        { ...model.comments[idx], likedByCurrentUser: msg.liked, likeCount: msg.count },
        ...model.comments.slice(idx + 1),
      ];
      return {
        model: { ...model, comments: updated },
        effects: [],
      };
    }

    case 'LikeFailed': {
      // Revert optimistic update
      const commentIndex = model.comments.findIndex(c => c.id === msg.commentId);
      if (commentIndex === -1) {
        return { model, effects: [] };
      }
      const comment = model.comments[commentIndex];
      const revertedCount = comment.likedByCurrentUser
        ? comment.likeCount - 1
        : comment.likeCount + 1;
      const revertedComment: typeof comment = {
        ...comment,
        likedByCurrentUser: !comment.likedByCurrentUser,
        likeCount: Math.max(0, revertedCount),
      };
      const updatedComments = [
        ...model.comments.slice(0, commentIndex),
        revertedComment,
        ...model.comments.slice(commentIndex + 1),
      ];
      return {
        model: {
          ...model,
          comments: updatedComments,
        },
        effects: [],
      };
    }

    default:
      // Compile-time exhaustiveness check
      return assertNever(msg);
  }
}